import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LegalLinks } from "@/components/FundingDisclosure";
import { Building, LayoutGrid, Search, FileText, Landmark, BadgeCheck, CreditCard, ShieldAlert, LogOut, Building2, FolderLock, CalendarClock, Trophy, Users, Calculator, PiggyBank, BellRing, ShieldCheck, Inbox, Sparkles, BookOpen, Clapperboard, Ruler, Gauge, Menu, FileSignature } from "lucide-react";
import { api } from "@/lib/api";
import { useTour } from "@/components/tour/TourProvider";
import { PageHelpButton } from "@/components/tour/PageHelpButton";
import { useSpanishScreens } from "@/lib/i18n";
import { Brand } from "@/components/Brand";
import { GuidePanel } from "@/components/GuidePanel";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { InstallAppButton } from "@/components/InstallAppButton";
import { BottomBar } from "@/components/BottomBar";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutGrid, end: true, id: "overview" },
  { to: "/dashboard/company", label: "Company & Team", icon: Building2, id: "company" },
  { to: "/dashboard/funding", label: "Funding Readiness", icon: Gauge, id: "funding" },
  { to: "/dashboard/grants", label: "Grant Finder", icon: Search, id: "grants" },
  { to: "/dashboard/proposals", label: "Proposal Writer", tag: "01", icon: FileText, id: "proposals" },
  { to: "/dashboard/budget", label: "Budget Forecaster", tag: "01", icon: Calculator, id: "budget" },
  { to: "/dashboard/blueprint", label: "Compliance Blueprint", tag: "IV", icon: Ruler, id: "blueprint" },
  { to: "/dashboard/licensing", label: "State Licensing", tag: "02", icon: Landmark, id: "licensing" },
  { to: "/dashboard/capital", label: "Capital Matchmaker", tag: "02", icon: PiggyBank, id: "capital" },
  { to: "/dashboard/lease-loi", label: "Lease LOI Builder", tag: "01", icon: FileSignature, id: "lease-loi" },
  { to: "/dashboard/portfolio", label: "Property Portfolio", icon: Building, id: "portfolio" },
  { to: "/dashboard/alerts", label: "Smart-Match Alerts", tag: "02", icon: BellRing, id: "alerts" },
  { to: "/dashboard/documents", label: "Document Vault", icon: FolderLock, id: "documents" },
  { to: "/dashboard/calendar", label: "Compliance Calendar", icon: CalendarClock, id: "calendar" },
  { to: "/dashboard/awards", label: "Awards & Milestones", icon: Trophy, id: "awards" },
  { to: "/dashboard/reviews", label: "Human Review", icon: BadgeCheck, id: "reviews" },
  { to: "/dashboard/pros", label: "Pros & Verified Badge", icon: ShieldCheck, id: "pros" },
  { to: "/dashboard/billing", label: "Plans & Billing", icon: CreditCard, id: "billing" },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [guideOpen, setGuideOpen] = useState(false);
  const [inbox, setInbox] = useState({ owner: false, count: 0 });
  const { pathname } = useLocation();
  const { start: startTour, offerTour, lang, setLang } = useTour();
  useSpanishScreens(lang === "es");
  const tourName = user?.role === "admin" && pathname.startsWith("/dashboard/master-admin") ? "admin" : "user";
  useEffect(() => { if (user?.role === "user" || (user?.role === "admin" && tourName === "admin")) { const t = setTimeout(() => offerTour(tourName), 1500); return () => clearTimeout(t); } }, [user, tourName, offerTour]);
  useEffect(() => { if (user && user.role === "user" && pathname !== "/dashboard/inquiries") api.get("/inquiries/unread").then((r) => setInbox(r.data)).catch(() => {}); }, [user, pathname]);
  const nav = inbox.owner ? [...NAV.slice(0, -1), { to: "/dashboard/inquiries", label: "Lender Inquiries", icon: Inbox, id: "inquiries", count: inbox.count }, NAV[NAV.length - 1]] : NAV;
  const items = user?.role === "admin" ? [...nav, { to: "/dashboard/admin", label: "Editor Queue", icon: ShieldAlert, id: "admin" }, { to: "/dashboard/master-admin", label: "Master Admin", icon: Users, id: "master-admin" }, { to: "/dashboard/content-library", label: "Content Library", icon: Clapperboard, id: "content-library" }, { to: "/dashboard/playbook", label: "Team Playbook", icon: BookOpen, id: "playbook" }] : nav;
  useEffect(() => {
    const t = sessionStorage.getItem("gv_invite");
    if (t) {
      sessionStorage.removeItem("gv_invite");
      navigate(`/invite/${t}`);
    }
  }, [navigate]);
  const doLogout = async () => {
    await logout();
    navigate("/");
  };
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  const side = { items, inbox, setInbox, user, lang, setLang, tourName, startTour, doLogout, closeMenu: () => setMenuOpen(false) };
  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-50">
      <header className="lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200 flex items-center justify-between px-4 h-14" data-testid="mobile-topbar">
        <Brand to="/dashboard" />
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <button className="h-11 w-11 -mr-2 inline-flex items-center justify-center rounded-md text-slate-700 hover:bg-slate-100" aria-label="Open menu" data-testid="mobile-menu-button"><Menu className="h-6 w-6" /></button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[85vw] max-w-xs p-0 flex flex-col gap-0 z-[9995]" data-testid="mobile-menu">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="p-5"><Brand to="/dashboard" /></div>
            <Sidebar {...side} mobile />
          </SheetContent>
        </Sheet>
      </header>
      <aside className="hidden lg:flex lg:w-64 lg:min-h-screen bg-white border-r border-slate-200 flex-col sticky top-0 h-screen">
        <div className="p-5"><Brand to="/dashboard" /></div>
        <Sidebar {...side} />
      </aside>
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-40 sm:pt-8 lg:py-12">
          <Outlet context={{ openGuide: () => setGuideOpen(true) }} />
          <PageHelpButton />
          <footer className="mt-16 pt-6 border-t border-slate-200" data-testid="dashboard-legal-footer"><LegalLinks partner={false} /></footer>
        </div>
      </main>
      <GuidePanel open={guideOpen} setOpen={setGuideOpen} />
      <BottomBar lang={lang} />
    </div>
  );
}

function Sidebar({ items, inbox, setInbox, user, lang, setLang, tourName, startTour, doLogout, mobile, closeMenu }) {
  const p = mobile ? "m-" : "";
  return (
    <>
      <div className="px-3 empty:hidden"><WorkspaceSwitcher /></div>
      <nav className="flex flex-col gap-0.5 px-3 py-3 overflow-y-auto flex-1">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`${p}nav-${n.id}`} onClick={n.id === "inquiries" ? () => setInbox({ ...inbox, count: 0 }) : undefined}
            className={({ isActive }) => `flex items-center gap-3 px-3 ${mobile ? "py-3" : "py-2"} rounded-md text-sm whitespace-nowrap transition-colors duration-150 ${isActive ? "bg-emerald-50 text-emerald-800 font-semibold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
            <n.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate" title={n.label}>{n.label}</span>
            {n.count > 0 && <span className="text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 py-0.5" data-testid={`${p}nav-inquiries-unread`}>{n.count}</span>}
            {n.tag && <span className="font-mono text-[10px] text-slate-400">{n.tag}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-200">
        <div className="text-sm font-semibold text-slate-900 truncate" data-testid={`${p}sidebar-user-name`} data-no-translate>{user?.name}</div>
        <div className="text-xs text-slate-500 truncate mb-3" data-no-translate>{user?.email}</div>
        <div className="flex items-center gap-1 mb-3 text-xs" data-testid={`${p}app-lang-switch`} data-no-translate>
          {[["en", "English"], ["es", "Español"]].map(([k, l]) => <button key={k} onClick={() => setLang(k)} data-testid={`${p}app-lang-${k}`} className={`px-2.5 py-1 rounded-full border transition-colors ${lang === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}>{l}</button>)}
        </div>
        {mobile && <InstallAppButton />}
        <button onClick={() => { closeMenu(); setTimeout(() => startTour(tourName), mobile ? 350 : 0); }} data-testid={`${p}start-tour-button`} className="flex items-center gap-2 text-sm text-emerald-700 font-semibold hover:text-emerald-800 transition-colors mb-2">
          <Sparkles className="h-4 w-4" /> {lang === "es" ? (tourName === "admin" ? "Recorrido de administración" : "Recorrido con Vera") : tourName === "admin" ? "Admin tour with Vera" : "Take the tour with Vera"}
        </button>
        <button onClick={doLogout} data-testid={`${p}logout-button`} className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );
}

export function PageHeader({ eyebrow, title, children, action }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 sm:mb-10 fade-up">
      <div>
        <div className="eyebrow mb-2">{eyebrow}</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 break-words">{title}</h1>
        {children && <p className="mt-3 text-slate-600 max-w-2xl">{children}</p>}
      </div>
      {action && <div className="flex flex-wrap gap-2 [&>*]:max-w-full">{action}</div>}
    </div>
  );
}
